import { useState, useEffect, useRef } from 'react';
import useLatestCallback from '@/hooks/useLatestCallback';
import {
  MessageCircle, Send, X, Minimize2, Maximize2, Phone,
  User, Store, Car, Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import axios from 'axios';
import { useAuthStore } from '@/store';

import { API } from '@/config/api';

// Message bubble component
const MessageBubble = ({ message, isOwnMessage }) => {
  const getIcon = () => {
    switch (message.sender_type) {
      case 'restaurant': return Store;
      case 'driver': return Car;
      default: return User;
    }
  };
  
  const Icon = getIcon();
  
  return (
    <div className={cn(
      "flex gap-2 mb-3",
      isOwnMessage ? "flex-row-reverse" : "flex-row"
    )}>
      <Avatar className="h-8 w-8 flex-shrink-0">
        <AvatarFallback className={cn(
          message.sender_type === 'restaurant' ? "bg-orange-100 text-orange-600" :
          message.sender_type === 'driver' ? "bg-blue-100 text-blue-600" :
          "bg-green-100 text-green-600"
        )}>
          <Icon className="h-4 w-4" />
        </AvatarFallback>
      </Avatar>
      
      <div className={cn(
        "max-w-[70%]",
        isOwnMessage ? "text-right" : "text-left"
      )}>
        <p className="text-xs text-muted-foreground mb-1">
          {message.sender_name}
        </p>
        <div className={cn(
          "rounded-2xl px-4 py-2 inline-block",
          isOwnMessage 
            ? "bg-primary text-primary-foreground rounded-br-md" 
            : "bg-muted rounded-bl-md"
        )}>
          <p className="text-sm">{message.content}</p>
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </p>
      </div>
    </div>
  );
};

// Quick reply buttons
const QuickReplies = ({ replies, onSelect }) => {
  return (
    <div className="flex flex-wrap gap-2 p-2 border-t bg-muted/50">
      {replies.map((reply, idx) => (
        <Button
          key={idx}
          variant="outline"
          size="sm"
          className="text-xs"
          onClick={() => onSelect(reply)}
        >
          {reply}
        </Button>
      ))}
    </div>
  );
};

export default function OrderChat({ orderId, onClose, minimized = false }) {
  const { token, user } = useAuthStore();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [quickReplies, setQuickReplies] = useState([]);
  const [isMinimized, setIsMinimized] = useState(minimized);
  const [participants, setParticipants] = useState({});
  const messagesEndRef = useRef(null);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchChatHistory = useLatestCallback(async () => {
    try {
      const response = await axios.get(`${API}/chat/order/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data.messages || []);
      setParticipants(response.data.participants || {});
    } catch (error) {
      console.error('Failed to fetch chat:', error);
    } finally {
      setLoading(false);
    }
  });

  useEffect(() => {
    fetchChatHistory();
    fetchQuickReplies();
  }, [orderId, fetchChatHistory]);

  const fetchQuickReplies = async () => {
    try {
      const response = await axios.get(`${API}/chat/quick-replies/customer`);
      setQuickReplies(response.data.quick_replies || []);
    } catch (error) {
      console.error('Failed to fetch quick replies:', error);
    }
  };

  const sendMessage = async (content) => {
    if (!content.trim()) return;
    
    setSending(true);
    try {
      const response = await axios.post(
        `${API}/chat/order/${orderId}/send`,
        { content: content.trim(), message_type: 'text' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setMessages(prev => [...prev, response.data.message]);
        setNewMessage('');
      }
    } catch (error) {
      console.error('Failed to send message:', error);
    } finally {
      setSending(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(newMessage);
  };

  if (isMinimized) {
    return (
      <div 
        className="fixed bottom-4 right-4 z-50"
        onClick={() => setIsMinimized(false)}
      >
        <Button className="rounded-full h-14 w-14 shadow-lg">
          <MessageCircle className="h-6 w-6" />
          {messages.filter(m => !m.read && m.sender_id !== user?.id).length > 0 && (
            <Badge className="absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center">
              {messages.filter(m => !m.read && m.sender_id !== user?.id).length}
            </Badge>
          )}
        </Button>
      </div>
    );
  }

  return (
    <Card className="fixed bottom-4 right-4 w-80 md:w-96 shadow-xl z-50 flex flex-col max-h-[500px]">
      {/* Header */}
      <CardHeader className="py-3 px-4 border-b flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Avatar className="h-8 w-8">
              <AvatarFallback className="bg-orange-100 text-orange-600">
                <Store className="h-4 w-4" />
              </AvatarFallback>
            </Avatar>
            <div>
              <CardTitle className="text-sm">Order #{orderId} Chat</CardTitle>
              <p className="text-xs text-muted-foreground">
                {participants.restaurant?.name || 'Restaurant'}
              </p>
            </div>
          </div>
          <div className="flex gap-1">
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-8 w-8"
              onClick={() => setIsMinimized(true)}
            >
              <Minimize2 className="h-4 w-4" />
            </Button>
            {onClose && (
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-8 w-8"
                onClick={onClose}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </CardHeader>

      {/* Messages */}
      <CardContent className="flex-1 overflow-y-auto p-4 min-h-[200px]">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <MessageCircle className="h-12 w-12 text-muted-foreground/30 mb-2" />
            <p className="text-sm text-muted-foreground">No messages yet</p>
            <p className="text-xs text-muted-foreground">Start the conversation!</p>
          </div>
        ) : (
          <>
            {messages.map((msg, idx) => (
              <MessageBubble 
                key={idx} 
                message={msg} 
                isOwnMessage={msg.sender_id === user?.id}
              />
            ))}
            <div ref={messagesEndRef} />
          </>
        )}
      </CardContent>

      {/* Quick Replies */}
      {quickReplies.length > 0 && (
        <QuickReplies 
          replies={quickReplies.slice(0, 3)} 
          onSelect={(reply) => sendMessage(reply)}
        />
      )}

      {/* Input */}
      <form onSubmit={handleSubmit} className="p-3 border-t flex gap-2">
        <input
          type="text"
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          placeholder="Type a message..."
          className="flex-1 px-3 py-2 text-sm border rounded-full focus:outline-none focus:ring-2 focus:ring-primary"
          disabled={sending}
        />
        <Button 
          type="submit" 
          size="icon" 
          className="rounded-full"
          disabled={!newMessage.trim() || sending}
        >
          {sending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Send className="h-4 w-4" />
          )}
        </Button>
      </form>
    </Card>
  );
}
